import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: CODE_REPO.
 *
 * Source-control repository shared by GitHub, GitLab, Bitbucket, and
 * Azure DevOps. Captures the cross-provider compliance 80%: visibility,
 * archived state, default branch, fork lineage, and the high-level
 * security toggles (secret scanning, default-branch protection summary).
 *
 * The detailed branch-protection schema stays on `BRANCH_PROTECTION` (a
 * separate canonical), because providers diverge sharply on required
 * reviews, status checks, signing, push restrictions, etc. CODE_REPO
 * surfaces a boolean `defaultBranchProtected` so a quick auditor query
 * ("show me every repo where the default branch is unprotected") can
 * run without joining BRANCH_PROTECTION rows.
 *
 * Provider-specific knobs (GitHub `security_and_analysis` sub-fields,
 * GitLab `merge_method`, Bitbucket `project.key`, Azure DevOps
 * `isDisabled`) go on `providerExtensions` when a check needs them.
 */

export const CODE_REPO_SCHEMA_VERSION = 1;

export const CodeRepo = z.object({
  /** Stable provider identifier. GitHub = repo node id or `repo:<id>`,
   *  GitLab = `gitlab:<projectId>`, Bitbucket = repo uuid,
   *  Azure DevOps = repo guid. */
  externalId: z.string(),

  /** Short repo name (no namespace). */
  name: z.string(),

  /** Full path including org/group/workspace + repo, e.g. `acme/web`,
   *  `acme/platform/api`, `acme-ws/api`, `acme-project/api`. */
  fullName: z.string(),

  /** Default branch name (e.g. `main`, `master`). Null when the repo is
   *  empty or the provider didn't expose it. */
  defaultBranch: z.string().nullable(),

  /** Cross-provider visibility — GitLab carries the third tier
   *  (`internal` = signed-in users only). */
  visibility: z.enum(['public', 'private', 'internal']),

  /** True when the repo is archived (read-only). */
  archived: z.boolean(),

  /** True when the repo is disabled — GitLab + GitHub expose this
   *  separately from archived. False for providers that don't model it. */
  disabled: z.boolean(),

  /** True when the repo is a fork. Null when the provider doesn't
   *  surface fork lineage. */
  isFork: z.boolean().nullable(),

  /** Parent grouping identifier (GitHub org login, GitLab group id,
   *  Bitbucket workspace slug, Azure DevOps project id). Stable. */
  parentId: z.string().nullable(),

  /** Parent grouping display name when distinct from the id. */
  parentName: z.string().nullable(),

  /** Default-branch protection summary. True when the provider reports
   *  at least one protection rule covering the default branch. The full
   *  policy lives on BRANCH_PROTECTION canonical rows. Null when the
   *  projector didn't fetch protection (signals lossy=true). */
  defaultBranchProtected: z.boolean().nullable(),

  /** Provider-side secret scanning toggle. Null when not surfaced. */
  secretScanningEnabled: z.boolean().nullable(),

  /** When the repo was created in the source system. */
  createdAt: z.string().datetime().nullable(),

  /** Most-recent push / commit / activity. Used for the "active repos"
   *  cut in SOC 2 evidence sets. */
  lastActiveAt: z.string().datetime().nullable(),

  /** Web URL where an auditor can open the repo. */
  webUrl: z.string().nullable(),

  /** Escape hatch for provider-specific fields. See RFC-179 §5.3 for
   *  the decision table. */
  providerExtensions: z.record(z.unknown()).optional(),
});

export type CodeRepo = z.infer<typeof CodeRepo>;
